/**
 * # SegmentedControl
 *
 * A component for selecting between mutually exclusive options, displayed as a horizontal group of buttons.
 * Useful for toggling between views, modes, or filter options.
 *
 * @see [Design ticket]{@link https://github.com/albert-io/nexus/issues/15199}
 *
 * ## Props
 *
 * - `options: OptionObject[] | ReactNode[]` - Array of options to display. Can be either:
 *   - Objects with `value`, `label`, and optional `disabled` properties
 *   - JSX elements for custom button content
 * - `onSelect?: (value: string | number) => void` - Callback fired when an option is selected.
 *   - When using `OptionObject[]`, receives the selected option's `value`
 *   - When using `ReactNode[]`, receives the selected option's index
 * - `className?: string` (optional) - Additional CSS class name for the container.
 * - `defaultSelectedIdx?: number` - Index of the initially selected option. Default is `0`.
 * - `disabled?: boolean` - Whether the entire control is disabled. Default is `false`.
 * - `disabledIndices?: number[]` - Array of indices to disable individually (for ReactNode options).
 *
 * ## Usage
 *
 * ### With value/label options
 *
 * ```tsx
 * import SegmentedControl from './SegmentedControl';
 *
 * const ViewToggle = () => {
 *   return (
 *     <SegmentedControl
 *       options={[
 *         { value: 'grid', label: 'Grid View' },
 *         { value: 'list', label: 'List View' },
 *         { value: 'table', label: 'Table View', disabled: true }
 *       ]}
 *       onSelect={(value) => console.log('Selected:', value)}
 *       defaultSelectedIdx={0}
 *     />
 *   );
 * };
 * ```
 *
 * ### With custom JSX elements
 *
 * ```tsx
 * import SegmentedControl from './SegmentedControl';
 * import { Icon } from '@albert-io/atomic';
 *
 * const LanguageToggle = () => {
 *   return (
 *     <SegmentedControl
 *       options={[
 *         <span key="en">English <Icon icon="check" /></span>,
 *         <span key="es">Spanish</span>
 *       ]}
 *       onSelect={(idx) => console.log('Selected index:', idx)}
 *       disabledIndices={[1]} // Disable Spanish option
 *     />
 *   );
 * };
 * ```
 *
 * ### Disabled state (entire control)
 *
 * ```tsx
 * <SegmentedControl
 *   options={[
 *     { value: 'on', label: 'On' },
 *     { value: 'off', label: 'Off' }
 *   ]}
 *   disabled
 * />
 * ```
 *
 * ### Individual disabled options
 *
 * ```tsx
 * <SegmentedControl
 *   options={[
 *     { value: 'free', label: 'Free' },
 *     { value: 'pro', label: 'Pro', disabled: true },
 *     { value: 'enterprise', label: 'Enterprise' }
 *   ]}
 * />
 * ```
 */

import Button from '@albert-io/atomic/atoms/Button/Button.react';
import classNames from 'classnames';
import type {ReactNode} from 'react';
import React, {isValidElement, useState} from 'react';

import './segmented-control.scss';

interface OptionObject {
  value: string | number;
  label: ReactNode;
  disabled?: boolean;
  /** Accessible name for the option's button when the visible label is abbreviated (e.g. "RAD" → "Radians"). */
  ariaLabel?: string;
}

interface SegmentedControlProps {
  options: OptionObject[] | ReactNode[];
  onSelect?: (value: string | number) => void;
  className?: string;
  /** Additional CSS class name for the button elements */
  buttonClassName?: string;
  defaultSelectedIdx?: number;
  /**
   * Controlled selected index. When provided, the component becomes controlled
   * and the visual selection is driven entirely by this prop.
   * Use together with `onSelect` to update the value.
   */
  selectedIdx?: number;
  /** Disables the entire control */
  disabled?: boolean;
  /** Array of indices to disable individually (for ReactNode options) */
  disabledIndices?: number[];
  // whether or not to change this component's background color to a darker shade of the parent
  blendDark?: boolean;
  /** Names the options as a group for assistive technology. Without it the control has no group role. */
  ariaLabel?: string;
}

const SegmentedControl = ({
  options,
  onSelect,
  className,
  buttonClassName,
  defaultSelectedIdx = 0,
  selectedIdx,
  disabled = false,
  disabledIndices = [],
  blendDark = false,
  ariaLabel
}: SegmentedControlProps) => {
  const isControlled = selectedIdx !== undefined;
  const [internalIdx, setInternalIdx] = useState(defaultSelectedIdx);
  const selectedOptionIdx = isControlled ? selectedIdx : internalIdx;
  const setSelectedOptionIdx = isControlled ? () => {} : setInternalIdx;

  return (
    <div
      role={ariaLabel ? 'group' : undefined}
      aria-label={ariaLabel}
      className={classNames('segmented-control', className, {
        'segmented-control--disabled': disabled,
        'segmented-control--blend-dark': blendDark
      })}
    >
      {options.map((option, idx) => {
        // If it's a JSX element, render it directly
        if (isValidElement(option)) {
          const isItemDisabled = disabled || disabledIndices.includes(idx);

          return (
            <Button
              // oxlint-disable-next-line react/no-array-index-key -- fixed-order segmented-control options; JSX-element options have no stable id
              key={idx}
              aria-pressed={selectedOptionIdx === idx}
              color='secondary'
              variant='solid'
              size='s'
              disabled={isItemDisabled}
              onClick={() => {
                if (!isItemDisabled) {
                  setSelectedOptionIdx(idx);
                  onSelect?.(idx);
                }
              }}
              className={classNames('segmented-control_btn', buttonClassName, {
                'group segmented-control_btn--selected': selectedOptionIdx === idx
              })}
            >
              {option}
            </Button>
          );
        }

        // Otherwise, treat it as an OptionObject
        const {
          value,
          label,
          disabled: optionDisabled,
          ariaLabel: optionAriaLabel
        } = option as OptionObject;
        const isItemDisabled = disabled || optionDisabled || disabledIndices.includes(idx);

        const onClick = () => {
          if (!isItemDisabled) {
            onSelect?.(value);
            setSelectedOptionIdx(idx);
          }
        };

        return (
          <Button
            key={value}
            aria-label={optionAriaLabel}
            aria-pressed={selectedOptionIdx === idx}
            color='secondary'
            variant='solid'
            size='s'
            onClick={onClick}
            className={classNames('segmented-control_btn', buttonClassName, {
              'segmented-control_btn--selected': selectedOptionIdx === idx
            })}
            disabled={isItemDisabled}
          >
            {isValidElement(option) ? option : label}
          </Button>
        );
      })}
    </div>
  );
};

SegmentedControl.displayName = 'SegmentedControl';

export default SegmentedControl;
